import { createSelector } from 'reselect';

import styleVariables from '../../_variables.scss';

import { SELECT_COUNTRY_LABEL } from '../../constants/country';
import { PAGE_KEY_MAP } from '../../constants/experience';
import { ROUTE_NAMES } from '../../constants/routes';
import { ExperiencePageKeyEnum, IExperience, IExperiencePageOverride } from '../../types/api';
import { CountryReasonEnum } from '../../types/country';
import { IBrandingLocationState, IShippingRules } from '../../types/shell';
import { IAppState } from '../../types/store';
import { getCountryDisplayName, isUnknownCountry, isUsCountry } from '../../util/country';
import { getColor, nullifyEmpty, resolveBrandingRoute } from '../../util/helpers';

export const selectRootState = (state: IAppState) => state.init;

export const selectCampaigns = createSelector(selectRootState, (root) => root.campaigns);

export const selectErrorCode = createSelector(selectRootState, (root) => root.error?.error_code);

export const selectItems = createSelector(selectRootState, (root) => root.items);

export const selectIsDigital = createSelector(selectRootState, (root) => root.isDigital);

export const selectIsLoading = createSelector(selectRootState, (root) => root.isLoading);

export const selectPassword = createSelector(selectRootState, (root) => root.password);

export const select2FATo = createSelector(selectRootState, (root) => root.send2FATo);

export const selectCode = createSelector(selectRootState, (root) => root.code);

export const selectInitialAddress = createSelector(selectRootState, (root) => root.address || undefined);

export const selectOrgData = createSelector(selectRootState, (root) => root.org);

export const selectSenderData = createSelector(selectRootState, (root) => root.sender);

export const selectWhiteLabeling = createSelector(selectRootState, (root) => root.whiteLabeling);

export const selectIsFormSubmitted = createSelector(selectRootState, (root) => root.isFormSubmitted);

export const selectIsPYGFlow = createSelector(selectCampaigns, (campaigns) => campaigns?.length);

export const selectUserId = createSelector(selectSenderData, (sender) => sender?.sender_uid);

export const selectSenderCompanyName = createSelector(
  selectSenderData,
  (sender) => sender?.sender_company_name?.trim() || undefined,
);

export const selectShippingRules = createSelector(selectRootState, (root): IShippingRules | null => root.shippingRules);

export const selectTaxRequiredCountries = createSelector(
  selectShippingRules,
  (shippingRules) => shippingRules?.tax_required_countries,
);

export const selectExperience = createSelector(selectRootState, (state) => state.experience);

export const selectHasExperience = createSelector(selectExperience, (experience) => !!experience);

export const selectIsEmbed = createSelector(selectRootState, (state) => state.isEmbed);

export const selectCustomScenarios = createSelector(selectRootState, (state) => state.customScenarios);

export const selectDetectedCountry = createSelector(selectRootState, (state) => state.detectedCountry);

export const selectUserCountry = createSelector(selectRootState, (state) => state.userCountry);

export const selectPendingCountry = createSelector(selectRootState, (state) => state.pendingCountry);

export const selectIsCountryChanging = createSelector(selectRootState, (state) => state.isCountryChanging);

export const selectIsMarketplaceBlocked = createSelector(selectRootState, (state) => state.isMarketplaceBlocked);

export const selectPendingNotice = createSelector(selectRootState, (state) => state.pendingNotice);

export const selectActiveInitRequestId = createSelector(selectRootState, (state) => state.activeInitRequestId);

export const selectUserCountryHeaderValue = createSelector(
  selectPendingCountry,
  selectUserCountry,
  (pendingCountry, userCountry) => pendingCountry ?? userCountry,
);

export const selectHasSingleFixedAddress = createSelector(
  selectInitialAddress,
  (address) => address?.fixed_address?.length === 1,
);

export const selectIsCountryGated = createSelector(
  selectDetectedCountry,
  selectIsEmbed,
  selectHasSingleFixedAddress,
  (detectedCountry, isEmbed, hasSingleFixedAddress) => detectedCountry !== null && !isEmbed && !hasSingleFixedAddress,
);

export const selectEffectiveCountry = createSelector(
  selectUserCountry,
  selectDetectedCountry,
  (userCountry, detectedCountry) => userCountry ?? detectedCountry,
);

export const selectIsEffectiveCountryUs = createSelector(selectEffectiveCountry, isUsCountry);

export const selectCountryReason = createSelector(
  selectUserCountry,
  selectDetectedCountry,
  (userCountry, detectedCountry): CountryReasonEnum => {
    if (userCountry) {
      return CountryReasonEnum.Chosen;
    }

    return isUnknownCountry(detectedCountry) ? CountryReasonEnum.Unknown : CountryReasonEnum.Detected;
  },
);

export const selectEffectiveCountryName = createSelector(selectEffectiveCountry, getCountryDisplayName);

export const selectShippingToLabel = createSelector(
  selectEffectiveCountryName,
  (countryName) => countryName ?? SELECT_COUNTRY_LABEL,
);

export const selectUsOnlyCompanyName = createSelector(
  selectSenderCompanyName,
  selectOrgData,
  (senderCompanyName, org) => senderCompanyName || org?.name?.trim() || undefined,
);

const getBrandingRoute = (location: IAppState['router']['location']): string =>
  resolveBrandingRoute(location.pathname, location.state as IBrandingLocationState | undefined);

// PYG has no claimed campaign, so org white-labeling wins there.
const isOrgBrandedRoute = (location: IAppState['router']['location']) =>
  getBrandingRoute(location).includes(ROUTE_NAMES.pyg);

export const selectLegacyBaseColor = createSelector(
  selectOrgData,
  selectWhiteLabeling,
  // Below is workaround to avoid the issue with Reference Error https://github.com/facebook/create-react-app/issues/9936
  // It's impossible to import `selectPickedCampaign for some reason`
  (state: IAppState) => state,
  (orgInfo, whiteLabeling, { init: { campaigns }, form: { pickedCampaignId }, router: { location } }) => {
    const pickedCampaign = campaigns?.find(({ id }) => id === pickedCampaignId);

    // PYG and the marketplace catalog use org white-labeling: no campaign is claimed on either,
    // so a campaign's colours must not leak onto them.
    if (pickedCampaign && pickedCampaign.ds_header_color && !isOrgBrandedRoute(location)) {
      return getColor(pickedCampaign.ds_header_color);
    }

    if (whiteLabeling && whiteLabeling.ds_header_color) {
      return getColor(whiteLabeling.ds_header_color);
    }

    return getColor(orgInfo?.header_color, styleVariables.defaultBaseColor);
  },
);

const resolveExperienceField = <T>(
  experience: IExperience | null,
  location: IAppState['router']['location'],
  pick: (source: IExperiencePageOverride | IExperience | undefined) => T | undefined,
): T | undefined => {
  if (!experience) {
    return undefined;
  }
  const brandingRoute = getBrandingRoute(location);
  const currentRoute = `/${brandingRoute.split('/').at(-1)}`;
  const pageKey = PAGE_KEY_MAP[currentRoute] as ExperiencePageKeyEnum | undefined;
  const pageOverride = pageKey ? experience.pages?.[pageKey]?.overrides : undefined;

  return pick(pageOverride) ?? pick(experience);
};

export const selectExperienceBaseColor = createSelector(
  selectExperience,
  (state: IAppState) => state.router.location,
  (experience, location) =>
    getColor(
      resolveExperienceField(experience, location, (s) => nullifyEmpty(s?.header?.hero_section?.background_color)),
    ),
);

export const selectBaseColor = createSelector(
  selectExperience,
  selectExperienceBaseColor,
  selectLegacyBaseColor,
  (experience, experienceColor, legacyColor) => (experience ? experienceColor : legacyColor),
);

export const selectLegacyAccentColor = createSelector(
  selectOrgData,
  selectWhiteLabeling,
  // Below is workaround to avoid the issue with Reference Error https://github.com/facebook/create-react-app/issues/9936
  // It's impossible to import `selectPickedCampaign for some reason`
  (state: IAppState) => state,
  (orgInfo, whiteLabeling, { init: { campaigns }, form: { pickedCampaignId }, router: { location } }) => {
    const pickedCampaign = campaigns?.find(({ id }) => id === pickedCampaignId);

    if (pickedCampaign && pickedCampaign.ds_button_color && !isOrgBrandedRoute(location)) {
      return getColor(pickedCampaign.ds_button_color);
    }

    if (whiteLabeling && whiteLabeling.ds_button_color) {
      return getColor(whiteLabeling.ds_button_color);
    }

    return getColor(orgInfo?.button_color, styleVariables.defaultAccentColor);
  },
);

export const selectExperienceAccentColor = createSelector(
  selectExperience,
  (state: IAppState) => state.router.location,
  (experience, location) =>
    resolveExperienceField(experience, location, (s) => nullifyEmpty(s?.buttons?.action?.color)),
);

export const selectAccentColor = createSelector(
  selectExperience,
  selectExperienceAccentColor,
  selectLegacyAccentColor,
  (experience, experienceColor, legacyColor) => (experience ? experienceColor : legacyColor),
);

export const selectResultScreenColor = createSelector(selectExperience, selectBaseColor, (experience, baseColor) => {
  if (experience?.background?.color) {
    return experience.background.color;
  }

  if (baseColor?.toLowerCase() === styleVariables.defaultBaseColor?.toLowerCase()) {
    return styleVariables.defaultResultScreenColor;
  }

  return baseColor;
});
